import React, { useState, useMemo } from 'react';
import { Search, Calendar, Clock, FileText } from 'lucide-react';
import Pagination, { paginate } from '@/components/Common/Pagination';
import ViewToggle, { ViewMode } from '@/components/Common/ViewToggle';

export interface HistoryItem {
  id: string;
  title: string;
  createdAt: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  type: string;
  metadata?: Record<string, any>;
}

interface HistoryPanelProps {
  items: HistoryItem[];
  onItemClick: (item: HistoryItem) => void;
  selectedItemId?: string;
  itemsPerPage?: number;
  renderItemActions?: (item: HistoryItem) => React.ReactNode;
}

const HistoryPanel: React.FC<HistoryPanelProps> = ({
  items,
  onItemClick,
  selectedItemId,
  itemsPerPage = 10,
  renderItemActions,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // Filter items based on search and date range
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           item.id.toLowerCase().includes(searchQuery.toLowerCase());

      let matchesDate = true;
      if (dateFrom || dateTo) {
        const itemDate = new Date(item.createdAt).toISOString().split('T')[0];
        if (dateFrom && itemDate < dateFrom) matchesDate = false;
        if (dateTo && itemDate > dateTo) matchesDate = false;
      }

      return matchesSearch && matchesDate;
    });
  }, [items, searchQuery, dateFrom, dateTo]);

  // Pagination
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = useMemo(
    () => paginate(filteredItems, currentPage, itemsPerPage),
    [filteredItems, currentPage, itemsPerPage]
  );

  // Reset page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, dateFrom, dateTo]);

  const hasDateFilter = !!(dateFrom || dateTo);
  const clearDateFilter = () => {
    setDateFrom('');
    setDateTo('');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'processing':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            History
          </h2>
          <ViewToggle value={viewMode} onChange={setViewMode} />
        </div>

        {/* Search and Filter */}
        <div className="space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search history..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                       focus:ring-2 focus:ring-blue-500 focus:border-transparent
                       placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>
          
          {/* Date Range Filter */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  max={dateTo || undefined}
                  placeholder="From"
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <span className="text-xs text-gray-400 flex-shrink-0">to</span>
              <div className="relative flex-1">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  min={dateFrom || undefined}
                  placeholder="To"
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {hasDateFilter && (
                <button
                  type="button"
                  onClick={clearDateFilter}
                  className="flex-shrink-0 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 px-1"
                  title="Clear date filter"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Items List */}
      <div className="flex-1 overflow-y-auto p-4">
        {paginatedItems.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">
              {searchQuery || hasDateFilter ? 'No matching items found' : 'No history yet'}
            </p>
          </div>
        ) : (
          <div className={viewMode === 'card' ? 'grid grid-cols-1 sm:grid-cols-2 gap-3' : 'space-y-3'}>
          {paginatedItems.map((item) => (
            <button
              key={item.id}
              onClick={() => onItemClick(item)}
              className={`w-full text-left p-4 rounded-lg border transition-all duration-200
                ${selectedItemId === item.id
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 dark:text-white truncate">
                    {item.title}
                  </h3>
                  <div className="flex items-center mt-2 space-x-3">
                    <span className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                      <Clock className="h-3 w-3 mr-1" />
                      {formatDate(item.createdAt)}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </div>
                  {/* Progress bar for active jobs */}
                  {(item.status === 'processing' || item.status === 'pending') && item.metadata?.progress && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-600 dark:text-gray-400">{item.metadata.progress}</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                        <div
                          className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: item.metadata.progress }}
                        />
                      </div>
                    </div>
                  )}
                  {/* Extra actions (e.g. download for completed jobs) */}
                  {renderItemActions && (
                    <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                      {renderItemActions(item)}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 border-t border-gray-200 dark:border-gray-700">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      )}
    </div>
  );
};

export default HistoryPanel;
